import {
  INPUT_BASE_HEIGHT,
  INPUT_BOX_SHADOW,
  ThemeBorderRadius,
  ThemeColors,
  ThemeSpaces,
  hexToRgbaWithAlpha,
} from '../theme';
import {
  DARK_BACKGROUND_COLOR,
  DARK_SIMPLE_PLACEHOLDER_COLOR,
  DARK_VIEW_BACKGROUND_COLOR,
  FormTheme,
  ORANGE_PLACEHOLDER_COLOR,
  ThemeBoxShadowMap,
} from './formThemeConstants';

export const flexCenter = () => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

export const mask = (opacity = 90) => ({
  backgroundColor: hexToRgbaWithAlpha(ThemeColors.COLOR_WHITE2, opacity / 100),
  backdropFilter: 'blur(2px)',
});

export const absolute = (horizontal = 0, vertical = 0) => ({
  position: 'absolute',
  top: vertical,
  bottom: vertical,
  right: horizontal,
  left: horizontal,
});

export const spaceBetween = (space, type = 'horizontal') => ({
  [type === 'vertical' ? 'marginBottom' : 'marginRight']: space,
  '&:last-child': {
    [type === 'vertical' ? 'marginBottom' : 'marginRight']: 0,
  },
});

export const absoluteMiddleNextToRight = () => ({
  position: 'absolute',
  top: '50%',
  right: ThemeSpaces.SPACE_MD,
  transform: 'translate(0, -50%)',
});

export const absoluteMiddleToRight = () => ({
  position: 'absolute',
  top: '50%',
  right: -ThemeSpaces.SPACE_MD,
  transform: 'translate(100%, -50%)',
});

export const formItemSuffix = () => ({
  ...absoluteMiddleNextToRight(),

  img: {
    height: 16,
    width: 'auto',
  },
});

export const viewModeStyle = backgroundColor => ({
  backgroundColor,

  '&, &::after': {
    boxShadow: 'none !important',
  },

  '&, input': {
    cursor: 'initial',
    opacity: 1,
  },
});

const inputTextStyles = ({
  backgroundColor,
  viewBackgroundColor,
  placeholderColor,
  boxShadow,
}) => ({
  ...(backgroundColor && {
    backgroundColor,
  }),
  '& > .ant-input, &.ant-input': {
    '&::placeholder': {
      color: placeholderColor,
    },
  },
  '&.ant-input': {
    boxShadow,
  },
  '&.qol-input-view-mode, &.qol-input-view-mode:hover': {
    ...viewModeStyle(viewBackgroundColor),
  },
});

export const inputText = token => ({
  '&&': {
    // Default theme
    ...inputTextStyles({
      viewBackgroundColor: ThemeColors.COLOR_GREY9_9,
      placeholderColor: ThemeColors.COLOR_GREY_BLUISH,
      boxShadow: INPUT_BOX_SHADOW,
    }),
    '> .ant-input, &.ant-input': {
      height: INPUT_BASE_HEIGHT,
      fontWeight: 500,

      '&.ant-input-status-error:focus, &:focus, .ant-form-item-has-error &, .ant-form-item-has-error &:hover':
        {
          backgroundColor: 'transparent',
        },
      '&::placeholder': {
        fontWeight: 'normal',
      },
      '.ant-form-item-has-error &': {
        '&, &:hover': {
          borderColor: token.colorError,
        },
      },
    },
    '&.ant-input-disabled, &.ant-input-disabled:hover, &[disabled]': {
      opacity: 0.5,
    },

    // DarkSimple theme
    [`.${FormTheme.DarkSimple} &`]: {
      ...inputTextStyles({
        backgroundColor: DARK_BACKGROUND_COLOR,
        viewBackgroundColor: DARK_VIEW_BACKGROUND_COLOR,
        placeholderColor: DARK_SIMPLE_PLACEHOLDER_COLOR,
        boxShadow: ThemeBoxShadowMap[FormTheme.DarkSimple],
      }),
    },
    // DarkOrange theme
    [`.${FormTheme.DarkOrange} &`]: {
      ...inputTextStyles({
        backgroundColor: DARK_BACKGROUND_COLOR,
        viewBackgroundColor: DARK_VIEW_BACKGROUND_COLOR,
        placeholderColor: ORANGE_PLACEHOLDER_COLOR,
        boxShadow: ThemeBoxShadowMap[FormTheme.DarkOrange],
      }),
    },
    // DarkOrangeSimple theme
    [`.${FormTheme.DarkOrangeSimple} &`]: {
      ...inputTextStyles({
        backgroundColor: DARK_BACKGROUND_COLOR,
        viewBackgroundColor: DARK_VIEW_BACKGROUND_COLOR,
        placeholderColor: ORANGE_PLACEHOLDER_COLOR,
        boxShadow: ThemeBoxShadowMap[FormTheme.DarkOrangeSimple],
      }),
    },
  },
});

export const applyBoxShadowAfter = (token, extraRules = {}) => ({
  '&::after': {
    content: "' '",
    ...absolute(),
    boxShadow: INPUT_BOX_SHADOW,
    borderRadius: token.borderRadiusSM,
    pointerEvents: 'none',
    zIndex: 1,
    ...extraRules,
  },
});

export const absoluteMiddleCenter = () => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
});

export const faLike = (unicode, type = 'solid') => ({
  content: `"${unicode}"`,
  fontFamily: "'Font Awesome 5 Free'",
  fontWeight: type === 'regular' ? 400 : 900,
  WebkitFontSmoothing: 'antialiased',
  display: 'inline-block',
  fontStyle: 'normal',
  fontFeatureSettings: 'normal',
  fontVariant: 'normal',
  textRendering: 'auto',
  lineHeight: 1,
});

export const rightEdgeSkew = ({
  angle = -30,
  height = 16,
  shadow = 'none',
  backgroundColor = 'transparent',
  borderRadius = 0,
  marginRight = 0,
  paddingRight = 0,
}) => {
  const slopeWidth = height * Math.tan((Math.abs(angle) * Math.PI) / 180);
  const diff = Math.floor(height - slopeWidth);

  return {
    position: 'relative',
    backgroundColor,
    borderRadius,
    filter: `drop-shadow(${shadow})`,
    borderBottomRightRadius: 0,
    borderTopRightRadius: 0,
    marginRight: marginRight + height,
    paddingRight: paddingRight - height,

    '&::after': {
      content: "' '",
      position: 'absolute',
      top: 0,
      bottom: 0,
      right: -diff,
      width: height,
      zIndex: -2,
      transformOrigin: 'bottom left',
      transform: `skew(${angle}deg, 0deg)`,
      backgroundColor,
      borderRadius,
      borderBottomLeftRadius: 0,
      borderTopLeftRadius: 0,
    },
  };
};

export const textAfter = content => ({
  content: `"${content}"`,
  display: 'inline-block',
  marginLeft: '4px',
  color: 'inherit',
  fontSize: 'inherit',
  fontFamily: 'inherit',
  fontWeight: 'inherit',
  lineHeight: 1,
});

export const heightExtendingError = token => ({
  '.ant-form-item .ant-form-item-explain': {
    height: 'auto',
    marginBottom: token.marginXXS,
  },
});

export const shipmentPageInnerContentNoMargin = (token, innerContent) => ({
  [`.${innerContent}`]: {
    [`@media (max-width: ${token.screenMDMax}px)`]: {
      marginLeft: 0,
      marginRight: 0,
    },
  },
});

export const partnerAccessTopBar = token => ({
  marginBottom: ThemeSpaces.SPACE_NORM,
  height: 20,
  ...borderRadius({ size: token.borderRadiusSM, side: 'top' }),
  backgroundColor: ThemeColors.COLOR_BLUE2,
  color: token.colorWhite,
  fontWeight: 900,
  fontStyle: 'italic',
  textAlign: 'center',
  lineHeight: '20px',
  textTransform: 'uppercase',
});

const triangleCSS = (color, width, height, direction = 'up') => {
  const half = width / 2;

  const directionStyles = {
    up: {
      borderWidth: `0 ${half}px ${height}px ${half}px`,
      borderBottomColor: color,
    },
    down: {
      borderWidth: `${height}px ${half}px 0 ${half}px`,
      borderTopColor: color,
    },
    left: {
      borderWidth: `${half}px ${height}px ${half}px 0`,
      borderRightColor: color,
    },
    right: {
      borderWidth: `${half}px 0 ${half}px ${height}px`,
      borderLeftColor: color,
    },
  };

  return {
    width: 0,
    height: 0,
    borderStyle: 'solid',
    borderColor: 'transparent',
    ...directionStyles[direction],
  };
};

export const withTriangle = ({
  width = 18,
  height = 12,
  direction = 'up',
  borderWidth = 1,
  borderColor,
  backgroundColor,
  position,
  dropShadow,
  afterOverrides,
  applyOwnStyles = true,
}) => ({
  ...(applyOwnStyles && {
    position: 'relative',
    backgroundColor,
    border: `${borderWidth}px solid ${borderColor}`,
  }),

  ...(dropShadow && {
    filter: dropShadow,
  }),

  '&::before, &::after': {
    content: '" "',
    position: 'absolute',
    pointerEvents: 'none',
    zIndex: 'initial',
    top: 'initial',
    bottom: 'initial',
    left: 'initial',
    right: 'initial',
    ...position,
  },

  '&::before': triangleCSS(borderColor, width, height, direction),

  '&::after': {
    ...triangleCSS(
      backgroundColor,
      width - borderWidth * 2,
      height - borderWidth,
      direction
    ),
    marginRight: borderWidth,
    ...afterOverrides,
  },
});

export const flex1 = () => ({
  flex: 1,
  minWidth: 0,
  minHeight: 0,
});

// Shared header shell for the app's top headers (`NonLoggedHeader`,
// `MainMenuHeader`). `breakLg`/`breakSm` are the breakpoints at which the left
// padding changes; `token` supplies the extra-small breakpoint bounds.
export const header = ({ token, breakLg, breakSm }) => ({
  backgroundColor: ThemeColors.COLOR_WHITE2,
  borderRadius: ThemeBorderRadius.BORDER_RADIUS_MD,
  boxShadow: `-1px -1px 1px ${ThemeColors.COLOR_WHITE}, 6px 6px 15px ${hexToRgbaWithAlpha(
    ThemeColors.COLOR_GREY_BLUISH,
    0.35
  )}`,

  '> .ant-row': {
    height: '100%',
  },

  [`@media (min-width: ${breakLg}px)`]: {
    height: 50,
    paddingLeft: ThemeSpaces.SPACE_NORM_20,
  },
  [`@media (min-width: ${breakSm}px) and (max-width: ${breakLg - 1}px)`]: {
    height: 50,
    paddingLeft: ThemeSpaces.SPACE_NORM,
  },
  [`@media (max-width: ${breakSm - 1}px)`]: {
    height: 50,
    paddingLeft: ThemeSpaces.SPACE_NORM,
  },
  [`@media (min-width: ${token.screenXSMin}px) and (max-width: ${token.screenXSMax}px)`]:
    {
      paddingLeft: token.paddingSM,
    },
});

export const borderRadius = ({
  size = ThemeBorderRadius.BORDER_RADIUS_BASE,
  side = 'all',
}) => {
  const top = {
    borderTopLeftRadius: size,
    borderTopRightRadius: size,
  };
  const bottom = {
    borderBottomLeftRadius: size,
    borderBottomRightRadius: size,
  };
  const left = {
    borderTopLeftRadius: size,
    borderBottomLeftRadius: size,
  };
  const right = {
    borderTopRightRadius: size,
    borderBottomRightRadius: size,
  };

  switch (side) {
    case 'top':
      return top;
    case 'bottom':
      return bottom;
    case 'left':
      return left;
    case 'right':
      return right;
    case 'all':
    default:
      return { ...top, ...bottom };
  }
};

const listCardOdd = {
  backgroundColor: ThemeColors.COLOR_WHITE,
  boxShadow: `1px 1px 6px ${ThemeColors.COLOR_GREY8_1}`,
};

const listCardEven = {
  backgroundColor: ThemeColors.COLOR_GREY10,
  boxShadow: `0 -2px 0 ${ThemeColors.COLOR_WHITE}, 0 1px 0 ${hexToRgbaWithAlpha(
    ThemeColors.COLOR_GREY_BLUISH,
    0.2
  )}, 1px 1px 6px ${ThemeColors.COLOR_GREY8_1}`,
};

// `spacing: false` skips the default vertical gap for callers that set their
// own `margin` (the LESS `.list-card()` gap was overridden by such callers via
// source order; a flag reproduces that without a fragile shorthand conflict).
export const listCard = (token, { spacing = true } = {}) => ({
  display: 'flex',
  borderRadius: token.borderRadiusSM,
  ...(spacing && {
    marginBottom: ThemeSpaces.SPACE_NORM,
    '&:last-child': {
      marginBottom: 0,
    },
  }),

  '&:nth-child(odd), &.odd-index': listCardOdd,
  '&:nth-child(even), &.even-index': listCardEven,

  '&.active': {
    border: `1px solid ${ThemeColors.COLOR_BLUE2}`,
  },
});

export const listCardHover = () => ({
  backgroundColor: ThemeColors.COLOR_WHITE6,
});

export const listCardActive = () => ({
  backgroundColor: ThemeColors.COLOR_GREY9_4,
});

export const BTN_DEFAULT_SELECTOR =
  ':not(.ant-btn-primary):not(.ant-btn-color-primary):not(.ant-btn-background-ghost):not(.ant-btn-dashed):not(.ant-btn-link)';

export const defBtnDisabled = body => ({
  '&.ant-btn-disabled, &.disabled, &[disabled]': {
    '&, &:hover, &:focus': body,
  },
});
